// ** React Imports
import { Component } from 'react'

// ** Reactstrap Imports
import { Button, Card, CardBody } from 'reactstrap'

/**
 * Catches render/lifecycle errors anywhere below it so a single broken component
 * shows a readable message instead of unmounting the whole tree (white screen).
 *
 * Chunk-load failures are handled separately: after a deploy the browser can hold a
 * cached index.html pointing at hashed bundles that no longer exist, so the lazy
 * import 404s. Those are recovered with a single automatic reload, guarded by a
 * sessionStorage flag so a genuinely missing chunk can't cause a reload loop.
 */
const RELOAD_FLAG = 'auxvault:chunk-reload'

const isChunkLoadError = error => {
  if (!error) return false
  const name = error.name || ''
  const message = error.message || ''
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  )
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null, info: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // Always surface it — this is the signal that used to be swallowed by the white screen.
    console.error('[ErrorBoundary]', error, info?.componentStack)
    this.setState({ info })

    if (isChunkLoadError(error) && !sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.setItem(RELOAD_FLAG, '1')
      window.location.reload()
    }
  }

  componentDidMount() {
    // A render that got this far means the current bundle is intact.
    sessionStorage.removeItem(RELOAD_FLAG)
  }

  handleReload = () => {
    sessionStorage.removeItem(RELOAD_FLAG)
    window.location.reload()
  }

  render() {
    const { error, info } = this.state

    if (!error) {
      return this.props.children
    }

    const chunkError = isChunkLoadError(error)

    const heading = chunkError ? 'A new version is available' : 'Something went wrong'
    const chunkMessage = 'This page could not finish loading because the application was updated. Reloading should fix it.'
    const renderMessage = 'This page failed to render. The error below has also been logged to the browser console.'
    const message = chunkError ? chunkMessage : renderMessage
    const stack = info && info.componentStack ? `\n${info.componentStack}` : ''

    return (
      <div className='d-flex justify-content-center align-items-center p-2' style={{ minHeight: '60vh' }}>
        <Card style={{ maxWidth: '720px', width: '100%' }}>
          <CardBody>
            <h4 className='mb-1'>{heading}</h4>
            <p className='text-muted mb-1'>{message}</p>
            <pre
              className='bg-light p-1 rounded mb-1'
              style={{ whiteSpace: 'pre-wrap', maxHeight: '260px', overflow: 'auto', fontSize: '0.8rem' }}
            >
              {error.toString()}
              {stack}
            </pre>
            <Button color='primary' onClick={this.handleReload}>
              Reload page
            </Button>
          </CardBody>
        </Card>
      </div>
    )
  }
}

export default ErrorBoundary
